import { CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { base } from 'viem/chains'

import { SUSDS_POINTS_MULTIPLIER } from '../../../consts'
import { USDC_ACCOUNT_ENABLED } from '../../flags'
import { ChainConfigEntry, SavingsStatsItem } from '../../types'
import { sortSavingsAccounts } from '../../utils'
import { chainLogo, tokenLogos } from './assets'
import { createTokenDefinitionRepository } from '@/domain/token-repository/TokenRepository'
import { TokenDefinition } from '@/domain/types/TokenDefinition'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { skySavingsGeneralStatsQueryOptionsFactory } from '@/features/savings/data/general-stats-query/skySavings'
import { skySavingsBackedByOptions } from '@/features/savings/data/savings-charts/backed-by-query/skySavingsBackedByQuery'
import { susdsSavingsRateQueryOptions } from '@/features/savings/data/savings-charts/savings-rate-query/skySavings'
import { ssrAuthOracleConverterQueryOptions } from '@/features/savings/logic/savings-converters/ssrAuthOracleConverter'

const usdc = new TokenDefinition({
  symbol: TokenSymbol('USDC'),
  name: 'Circle USD',
  decimals: 6,
  address: CheckedAddress('0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'),
  logo: tokenLogos.usdc,
})

const usds = new TokenDefinition({
  symbol: TokenSymbol('USDS'),
  name: 'USDS Stablecoin',
  decimals: 18,
  address: CheckedAddress('0x820C137fa70C8691f0e44Dc420a5e53c168921Dc'),
  logo: tokenLogos.usds,
})

const susds = new TokenDefinition({
  symbol: TokenSymbol('sUSDS'),
  name: 'Savings USDS',
  decimals: 18,
  address: CheckedAddress('0x5875eEE11Cf8398102FdAd704C9E96607675467a'),
  logo: tokenLogos.susds,
})

const susdc = new TokenDefinition({
  symbol: TokenSymbol('sUSDC'),
  name: 'Savings USDC',
  decimals: 18,
  address: CheckedAddress('0x3128a0F7f0ea68E7B7c9B00AFa7E41045828e858'),
  logo: tokenLogos.susdc,
})

export const baseConfig: ChainConfigEntry = {
  originChainId: base.id,
  meta: {
    name: 'Base',
    logo: chainLogo,
  },
  tokenDefinitionRepository: createTokenDefinitionRepository([usdc, usds, susds, susdc]),
  nativeAssetInfo: {
    nativeAssetName: 'Ether',
    wrappedNativeAssetSymbol: TokenSymbol('WETH'),
    wrappedNativeAssetAddress: CheckedAddress('0x4200000000000000000000000000000000000006'),
    nativeAssetSymbol: TokenSymbol('ETH'),
    minRemainingNativeAssetBalance: NormalizedNumber(0.0005),
    nativeAssetLogo: tokenLogos.eth,
  },
  tokensWithMalformedApprove: [],
  tokensRequiringResetApproval: [],
  contractsRequiringUsdtResetApproval: [],
  permitSupport: {},
  sparkLend: undefined,
  savings: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
      ...(USDC_ACCOUNT_ENABLED ? { [susdc.symbol]: { type: 'vault' as const, assetsDecimals: 6 } } : {}),
    },
    accounts: sortSavingsAccounts([
      ...(USDC_ACCOUNT_ENABLED
        ? [
            {
              savingsToken: susdc.symbol,
              underlyingToken: usdc.symbol,
              supportedAssets: [usdc.symbol],
              fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
              savingsRateQueryOptions: susdsSavingsRateQueryOptions,
              stats: {
                queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susdc.symbol),
                items: ['tvl', 'users', 'liquidity-sky-psm'] satisfies SavingsStatsItem[],
              },
              backedByQueryOptions: skySavingsBackedByOptions,
              liquidityChartQueryOptions: undefined,
              sparkPointsMultiplier: undefined,
            },
          ]
        : []),
      {
        savingsToken: susds.symbol,
        underlyingToken: usds.symbol,
        supportedAssets: [usds.symbol, usdc.symbol],
        fetchConverterQuery: ssrAuthOracleConverterQueryOptions,
        savingsRateQueryOptions: susdsSavingsRateQueryOptions,
        stats: {
          queryOptions: skySavingsGeneralStatsQueryOptionsFactory(susds.symbol),
          items: ['tvl', 'users'] satisfies SavingsStatsItem[],
        },
        backedByQueryOptions: skySavingsBackedByOptions,
        liquidityChartQueryOptions: undefined,
        sparkPointsMultiplier: SUSDS_POINTS_MULTIPLIER,
      },
    ]),
    psmStables: [usdc.symbol, usds.symbol],
  },
  legacyFarms: undefined,
  spk: undefined,
  points: undefined,
  sparkRewards: undefined,
  swap: {
    priceConfigs: {
      [usdc.symbol]: { type: 'fixed-usd' },
      [usds.symbol]: { type: 'fixed-usd' },
      [susds.symbol]: { type: 'ssr-auth-oracle' },
    },
  },
}
